// ─── Itinéraire, position & aperçu carte (géo) ───────────────────
// Briques pures réutilisables. `expo-location` est chargé en `require` PROTÉGÉ :
// présent sur un build natif à jour → GPS ; absent (vieux build) → repli
// navigateur / null, JAMAIS de crash. La carte est un APERÇU statique OSM
// (image, sans clé, fonctionne aussi en OTA).
import { Linking, Platform } from 'react-native';
import { confirmerChoix } from '../services/confirm';

// URL d'itinéraire vers un lieu : coordonnées si dispo, sinon adresse texte.
export function lienItineraire({ lat, lng, adresse } = {}) {
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  const a = (adresse || '').trim();
  return a ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}` : '';
}

// ── Choix de l'application de navigation ─────────────────────────
// `lienItineraire` pointe Google Maps : l'OS ouvre donc directement cette
// app-là, sans jamais demander. On propose explicitement les alternatives.
//
// URL *universelles* (https / maps.apple.com) et non schémas privés
// (`comgooglemaps://`, `waze://`) : celles-ci ouvrent l'app installée et
// retombent sur le site sinon, SANS déclaration `LSApplicationQueriesSchemes`
// dans Info.plist — donc livrable en OTA, sans build natif.
export function optionsItineraire({ lat, lng, adresse } = {}) {
  const coords = (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng)))
    ? `${lat},${lng}` : '';
  const a = (adresse || '').trim();
  if (!coords && !a) return [];
  const dest = encodeURIComponent(coords || a);
  const opts = [];
  if (Platform.OS !== 'android') opts.push({ id: 'plans', label: 'Plans', url: `http://maps.apple.com/?daddr=${dest}` });
  opts.push({ id: 'google', label: 'Google Maps', url: `https://www.google.com/maps/dir/?api=1&destination=${dest}` });
  // Waze navigue aux coordonnées ; sans elles, on lui passe une recherche.
  opts.push({ id: 'waze', label: 'Waze', url: coords ? `https://waze.com/ul?ll=${coords}&navigate=yes` : `https://waze.com/ul?q=${dest}&navigate=yes` });
  return opts;
}

// Demande l'app puis ouvre. Une seule option → on ouvre sans rien demander.
export function demanderItineraire(cible, { titre = 'Itinéraire', message, annuler = 'Annuler' } = {}) {
  const opts = optionsItineraire(cible);
  if (!opts.length) return;
  const go = (u) => Linking.openURL(u).catch(() => {});
  if (opts.length === 1) { go(opts[0].url); return; }
  confirmerChoix(titre, message, [
    ...opts.map(o => ({ text: o.label, onPress: () => go(o.url) })),
    { text: annuler, style: 'cancel' },
  ]);
}

// Carte OSM interactive OFFICIELLE (embed, sans clé), centrée avec un marqueur.
// À charger dans une WebView (natif) ou une iframe (web). '' si pas de coordonnées.
export function lienCarteEmbed({ lat, lng } = {}, { delta = 0.0022 } = {}) {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return '';
  const bbox = `${Number(lng) - delta},${Number(lat) - delta},${Number(lng) + delta},${Number(lat) + delta}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`;
}

// Aperçu carte STATIQUE (image, sans clé) — repli quand une WebView/iframe n'est
// pas disponible. Source Wikimedia (fiable). '' si pas de coordonnées.
export function lienCarteStatique({ lat, lng } = {}, { zoom = 16, w = 600, h = 280 } = {}) {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return '';
  return `https://maps.wikimedia.org/img/osm-intl,${zoom},${lat},${lng},${w}x${h}.png`;
}

// `expo-location` disponible ? (chargé paresseusement, protégé).
function chargerLocation() {
  try { return require('expo-location'); } catch { return null; }
}

// La géolocalisation est-elle plausible ? web (navigator) OU module expo-location.
export function positionDisponible() {
  if (typeof navigator !== 'undefined' && navigator.geolocation) return true;
  return !!chargerLocation();
}

// Position actuelle → { lat, lng } ou null (jamais de rejet). Ordre : expo-location
// (GPS natif / shim web), puis repli navigateur.
export async function positionActuelle() {
  const Location = chargerLocation();
  if (Location && Location.requestForegroundPermissionsAsync) {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') return null;
      const pos = await Location.getCurrentPositionAsync({});
      if (pos && pos.coords) return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch { /* module natif absent (vieux build) → repli navigateur */ }
  }
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  });
}
